import { Ops } from '@willie/schemas'
import type { Tables } from '@willie/supabase'
import { INSTALLATION_PROFILE_OPTIONS, type InstallationProfile, type MeterMaker } from './constants'

export type InstallationProfileCatalogRow = Tables<'ops_installation_profiles'>

export interface InstallationProfileCriteria {
  meterMaker: MeterMaker | null
  meterModel: string | null
}

interface RankedInstallationProfile {
  profile: InstallationProfile
  score: number
}

const PROFILE_ORDER: ReadonlyMap<string, number> = new Map(
  INSTALLATION_PROFILE_OPTIONS.map((profile, index) => [profile, index])
)

const criterionScore = <T extends string | number>(values: readonly T[], selected: T | null): number | null => {
  if (selected === null || values.length === 0) return 0
  return values.includes(selected) ? 1 : null
}

const profileScore = (
  profile: InstallationProfileCatalogRow,
  criteria: InstallationProfileCriteria
): number | null => {
  const scores = [
    criterionScore(profile.meter_makers, criteria.meterMaker),
    criterionScore(profile.meter_models, criteria.meterModel)
  ]
  if (scores.includes(null)) return null
  return scores.reduce<number>((total, score) => total + (score ?? 0), 0)
}

const toRankedProfile = (
  profile: InstallationProfileCatalogRow,
  criteria: InstallationProfileCriteria
): RankedInstallationProfile | null => {
  const parsedProfile = Ops.InstallationProfiles.installationProfileSchema.safeParse(profile.slug)
  if (!parsedProfile.success) return null
  const score = profileScore(profile, criteria)
  return score === null ? null : { profile: parsedProfile.data, score }
}

const isRankedProfile = (profile: RankedInstallationProfile | null): profile is RankedInstallationProfile =>
  profile !== null

// Falls back to the whole catalog when no profile fits the hardware, and always keeps the current one.
export const profileOptions = (
  profiles: readonly InstallationProfileCatalogRow[],
  criteria: InstallationProfileCriteria,
  currentProfile: InstallationProfile | null
): InstallationProfile[] => {
  const matches = profiles
    .map((profile) => toRankedProfile(profile, criteria))
    .filter(isRankedProfile)
    .sort(
      (left, right) =>
        right.score - left.score ||
        (PROFILE_ORDER.get(left.profile) ?? Number.MAX_SAFE_INTEGER) -
          (PROFILE_ORDER.get(right.profile) ?? Number.MAX_SAFE_INTEGER)
    )
    .map(({ profile }) => profile)

  const options = matches.length > 0 ? matches : profiles.map((profile) => profile.slug)
  return currentProfile && !options.includes(currentProfile) ? [currentProfile, ...options] : options
}
